import { useCallback, useEffect, useState } from "react";
import { useIsTabletScreen } from "./useIsSmallScreen";

/** Mirrors the package SideNav's RAIL_EXPANDED / RAIL_COLLAPSED. */
const EXPANDED_PX = "240px";
const COLLAPSED_PX = "72px";

const STORAGE_KEY = "outpave-sidebar-collapsed";

/**
 * Collapsed state for the app rail, persisted across reloads.
 *
 * The package `SideNav` is controlled only: it deliberately ships no
 * persistence, so the choice of where to keep this is ours. It lives in the
 * layout rather than the sidebar because the layout also has to offset its
 * content by the rail's width, and both need to read the same value.
 *
 * `navWidth` is meant to be written to `--layout-nav-width` on the layout root,
 * the nearest common ancestor of the rail and the content. Setting it there
 * rather than on `:root` keeps the single-writer rule (R3) intact and means
 * anything already reading the variable follows the rail with no extra wiring.
 */
export function useSidebarCollapsed() {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(STORAGE_KEY) === "true";
    } catch {
      // Private browsing and blocked site data both throw here.
      return false;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, String(collapsed));
    } catch {
      // Not being able to remember the choice is not worth breaking the page.
    }
  }, [collapsed]);

  const toggleCollapsed = useCallback(() => setCollapsed((c) => !c), []);

  // Three tiers, not two: below `md` the rail is a drawer and never collapsed,
  // `md` to `lg` it is forced to the icon strip with no toggle (the choice is
  // not the user's at that width), and above that it is theirs and remembered.
  // The tiers have to come from the same source the shells use, or the drawer
  // range and the strip range overlap and one of them never appears.
  const isTablet = useIsTabletScreen();
  const effectiveCollapsed = isTablet || collapsed;

  return {
    collapsed: effectiveCollapsed,
    // Undefined hides the toggle, which is how the package pins the rail.
    toggleCollapsed: isTablet ? undefined : toggleCollapsed,
    navWidth: effectiveCollapsed ? COLLAPSED_PX : EXPANDED_PX,
  };
}

export default useSidebarCollapsed;
